import { useSession } from "@src/appV2/Auth/api/useSession";
import { FacilityRoles, FacilityUserPermissions } from "@src/appV2/FacilityUser/types";
import { includes } from "lodash";
import { useMemo } from "react";

interface ShiftManagementPermissions {
  canPostShifts: boolean;
  canEditShiftTimes: boolean;
  canCancelShifts: boolean;
}

/**
 * Hook for checking if the current user has permissions to manage shifts.
 *
 * CBH admins and users with ADMIN or SHIFT_MANAGEMENT roles have full access.
 * Granular permissions (POST_SHIFT_PERMISSION, CANCEL_SHIFT_PERMISSION) allow
 * narrower access for other workplace users.
 */
export function useShiftManagementPermission(): ShiftManagementPermissions {
  const session = useSession();

  return useMemo(() => {
    const userPermissions = session.user?.permissions ?? [];
    const userRoles = session.user?.access ?? [];
    const isCbhAdmin = session.admin;
    const hasAdminOrShiftManagementRole =
      includes(userRoles, FacilityRoles.ADMIN) ||
      includes(userRoles, FacilityRoles.SHIFT_MANAGEMENT);
    const canPostShifts =
      isCbhAdmin ||
      hasAdminOrShiftManagementRole ||
      includes(userPermissions, FacilityUserPermissions.POST_SHIFT_PERMISSION);

    return {
      canPostShifts,
      canEditShiftTimes: canPostShifts,
      canCancelShifts:
        isCbhAdmin ||
        hasAdminOrShiftManagementRole ||
        includes(userPermissions, FacilityUserPermissions.CANCEL_SHIFT_PERMISSION),
    };
  }, [session.admin, session.user?.access, session.user?.permissions]);
}
